import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Heart, MessageCircle, Play, Layers } from 'lucide-react';
import { ProfileHeader } from '../components/profile/ProfileHeader';
import { useAuthStore } from '../store/authStore';
import { getUserProfile, getUserPosts } from '../lib/profileService';
import { supabase } from '../lib/supabase';
import { Post, User } from '../types';

export const ProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { user: currentUser } = useAuthStore();
  const [profileData, setProfileData] = useState<User | null>(null);
  const [userPosts, setUserPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine if this is the current user's profile
  const isCurrentUser = !username || (currentUser && (
    username === currentUser.email?.split('@')[0] || 
    username === currentUser.user_metadata?.username
  ));

  useEffect(() => {
    if (isCurrentUser && currentUser) {
      // Load current user's profile
      loadCurrentUserProfile();
    } else if (username) {
      // Load specific user's profile by username
      loadUserProfileByUsername(username);
    }
  }, [username, currentUser, isCurrentUser]);

  const loadCurrentUserProfile = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);
      
      const profile = await getUserProfile(currentUser.id);
      if (profile) {
        setProfileData(profile);
        await loadUserPosts(currentUser.id);
      } else {
        setError('Profile not found');
      }
    } catch (err) {
      console.error('Error loading current user profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadUserProfileByUsername = async (targetUsername: string) => {
    try {
      setLoading(true);
      setError(null);

      // Find user by username
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', targetUsername)
        .single();

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          setError('User not found');
        } else {
          throw profileError;
        }
        return;
      }

      // Get post count
      const { count: postCount } = await supabase
        .from('posts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', profiles.id);

      const user: User = {
        id: profiles.id,
        username: profiles.username,
        displayName: profiles.full_name || profiles.username,
        profileImage: profiles.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: profiles.bio || '',
        followerCount: 0,
        followingCount: 0,
        postCount: postCount || 0,
        isPrivate: profiles.is_private || false,
        website: profiles.website,
        location: profiles.location,
        joinedDate: profiles.created_at,
      };

      setProfileData(user);
      await loadUserPosts(profiles.id);
    } catch (err) {
      console.error('Error loading user profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadUserPosts = async (userId: string) => {
    try {
      setPostsLoading(true);
      const posts = await getUserPosts(userId, 12, 0);
      setUserPosts(posts);
    } catch (err) {
      console.error('Error loading user posts:', err);
    } finally {
      setPostsLoading(false);
    }
  };

  const handlePostDeleted = (postId: string) => {
    setUserPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
    // Update post count in profile
    if (profileData) {
      setProfileData({
        ...profileData,
        postCount: Math.max(0, profileData.postCount - 1)
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">{error || 'Profile not found'}</p>
          {username && (
            <p className="text-sm text-gray-500 mt-2">
              The user @{username} could not be found.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-16 md:pb-0 bg-white min-h-screen">
      {/* Profile Header */}
      <ProfileHeader user={profileData} isCurrentUser={isCurrentUser} />
      
      {/* Profile Content */}
      <div className="container mx-auto px-4 max-w-screen-lg">
        {/* Loading State for Posts */}
        {postsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            {/* Photo Grid */}
            {userPosts.length > 0 ? (
              <div className="grid grid-cols-3 gap-1 md:gap-6">
                {userPosts.map((post) => {
                  // Get the first media item or fallback to legacy imageUrl
                  const firstMedia = post.media && post.media.length > 0 ? post.media[0] : null;
                  const imageUrl = firstMedia?.media_url || (post as any).imageUrl;
                  const isVideo = firstMedia?.media_type === 'video';
                  const hasMultipleMedia = post.media && post.media.length > 1;

                  return (
                    <div key={post.id} className="relative pb-[100%] bg-gray-100 overflow-hidden group cursor-pointer">
                      {imageUrl ? (
                        <>
                          {isVideo ? (
                            <video
                              src={imageUrl}
                              className="absolute inset-0 w-full h-full object-cover"
                              muted
                            />
                          ) : (
                            <img 
                              src={imageUrl}
                              alt="Post"
                              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              onError={(e) => {
                                console.error('Profile post image failed to load:', imageUrl);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          
                          {/* Media Type Indicators */}
                          <div className="absolute top-2 right-2 flex space-x-1">
                            {isVideo && (
                              <div className="bg-black bg-opacity-50 rounded-full p-1">
                                <Play className="h-3 w-3 text-white fill-white" />
                              </div>
                            )}
                            {hasMultipleMedia && (
                              <div className="bg-black bg-opacity-50 rounded-full p-1">
                                <Layers className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </div>

                          {/* Media Count Badge */}
                          {hasMultipleMedia && (
                            <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                              {post.media.length}
                            </div>
                          )}

                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex items-center space-x-4 text-white">
                              <div className="flex items-center">
                                <Heart className="h-5 w-5 mr-1 fill-white" />
                                <span className="font-semibold">{post.like_count}</span>
                              </div>
                              <div className="flex items-center">
                                <MessageCircle className="h-5 w-5 mr-1 fill-white" />
                                <span className="font-semibold">{post.comment_count}</span>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100">
                          <p className="text-sm">No media</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                    <Heart className="h-8 w-8 text-gray-400" />
                  </div>
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">No posts yet</h3>
                {isCurrentUser ? (
                  <>
                    <p className="text-gray-500 mb-4">Share your first photo or video to get started!</p>
                    <a 
                      href="/create" 
                      className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                    >
                      Create your first post
                    </a>
                  </>
                ) : (
                  <p className="text-gray-500">No posts to show</p>
                )}
              </div>
            )}

            {/* Post Count Summary */}
            {userPosts.length > 0 && (
              <div className="text-center py-8 border-t border-gray-200 mt-8">
                <p className="text-sm text-gray-500">
                  Showing {userPosts.length} of {profileData.postCount} posts
                </p>
                {userPosts.length < profileData.postCount && (
                  <button 
                    onClick={() => {
                      // Load more posts functionality could be added here
                      console.log('Load more posts');
                    }}
                    className="mt-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    Load more posts
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};